import { useCallback, useEffect, useState } from 'react';

// ---- refreshable-page controls -------------------------------------------
// A refreshable page bakes its data in as JSON; "refresh" re-runs the stored
// SQL server-side and republishes the same URL (see the eve refresh routes).
// These owner-only controls surface that: they render ONLY when the eve GET
// refresh-state returns 200 (you own a refreshable page). A 404 — a non-owner,
// or a static page with no recipe (the route can't tell them apart, by design)
// — leaves the surface exactly as it was. Every failure degrades to "no
// controls". Shared by the in-chat PageCard (primitives.tsx) and the standalone
// /artifact/:id wrapper chrome (the artifact-chrome island), so the refresh
// logic lives in exactly one place.

export type RefreshInterval = 'manual' | 'daily';

/** Mirrors the eve `GET …/refresh-state` 200 body. `interval` is never null here
 *  (a null interval is the "not refreshable" 404 case, surfaced as no controls). */
export interface PageRefreshState {
  interval: RefreshInterval;
  status: 'ok' | 'failed' | null;
  errorClass: 'recoverable' | 'non_recoverable' | null;
  paused: boolean;
  lastRefreshedAt: string | null;
  running: boolean;
  cooldownUntil: string | null;
  canSchedule: boolean;
}

const REFRESH_POLL_MS = 4000;

/** Owner-facing refresh state + actions for a page id. Fetches refresh-state,
 *  polls while a run is in flight, and exposes the two POST actions. `state` is
 *  null until loaded and stays null on a 404 or any load error (no controls). */
export function usePageRefresh(pageId: string | null) {
  const [state, setState] = useState<PageRefreshState | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(false);

  // A 404 means "not refreshable / not owner" → drop to no-controls. A non-ok
  // (5xx) or network error throws so callers keep the prior state (a poll blip
  // shouldn't blank a working card); the initial load treats a throw as no-controls.
  const read = useCallback(async () => {
    if (!pageId) return;
    const res = await fetch(`/api/eve/v1/artifacts/${pageId}/refresh-state`, { credentials: 'same-origin' });
    if (res.status === 404) {
      setState(null);
      return;
    }
    if (!res.ok) throw new Error(`refresh-state ${res.status}`);
    setState((await res.json()) as PageRefreshState);
  }, [pageId]);

  useEffect(() => {
    if (!pageId) return;
    let alive = true;
    void read().catch(() => {
      if (alive) setState(null);
    });
    return () => {
      alive = false;
    };
  }, [pageId, read]);

  // Poll only while a run is in flight; the effect tears down when `running`
  // flips false (a settled read) or the card unmounts.
  useEffect(() => {
    if (!state?.running) return;
    const timer = window.setInterval(() => void read().catch(() => {}), REFRESH_POLL_MS);
    return () => window.clearInterval(timer);
  }, [state?.running, read]);

  const refreshNow = useCallback(async () => {
    if (!pageId) return;
    setBusy(true);
    setActionError(false);
    try {
      const res = await fetch(`/api/eve/v1/artifacts/${pageId}/refresh`, { method: 'POST', credentials: 'same-origin' });
      if (res.status === 202) {
        // Mark running so the poll effect starts; the server's lock is the real
        // guard, so a poll that reads running:false self-corrects the button.
        setState((s) => (s ? { ...s, running: true } : s));
      } else if (res.status === 429) {
        const body = (await res.json().catch(() => ({}))) as { cooldownUntil?: unknown };
        if (typeof body.cooldownUntil === 'string') {
          const until = body.cooldownUntil;
          setState((s) => (s ? { ...s, cooldownUntil: until } : s));
        }
      } else {
        setActionError(true);
      }
    } catch {
      setActionError(true);
    } finally {
      setBusy(false);
    }
  }, [pageId]);

  const setSchedule = useCallback(
    async (interval: RefreshInterval) => {
      if (!pageId) return;
      setBusy(true);
      setActionError(false);
      // Optimistic, with a synchronous revert on failure (mirrors changeVisibility):
      // a failed write must never strand the toggle on the un-persisted value.
      const prev = state?.interval;
      setState((s) => (s ? { ...s, interval } : s));
      try {
        const res = await fetch(`/api/eve/v1/artifacts/${pageId}/schedule`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ interval }),
        });
        if (!res.ok) throw new Error(`schedule ${res.status}`);
      } catch {
        if (prev !== undefined) setState((s) => (s ? { ...s, interval: prev } : s));
        setActionError(true);
      } finally {
        setBusy(false);
      }
    },
    [pageId, state?.interval],
  );

  return { state, busy, actionError, refreshNow, setSchedule };
}

function remainingMs(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, t - Date.now()) : 0;
}

/** Ticks down to zero (once a second) toward an ISO deadline; 0 when absent/past. */
function useCountdown(untilIso: string | null): number {
  const [ms, setMs] = useState(() => remainingMs(untilIso));
  useEffect(() => {
    setMs(remainingMs(untilIso));
    if (!untilIso) return;
    const timer = window.setInterval(() => {
      const r = remainingMs(untilIso);
      setMs(r);
      if (r <= 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [untilIso]);
  return ms;
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" / "Mar 4" — a compact "as of" line. */
function formatRelative(iso: string | null): string {
  if (!iso) return 'not yet';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'unknown';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function RefreshGlyph({ spinning }: { spinning: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className={spinning ? 'he-spin' : undefined}>
      <path d="M20 11a8 8 0 1 0-.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 4v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Compact on/off switch for the "refresh daily" schedule. */
function DailyToggle({ on, disabled, onToggle }: { on: boolean; disabled: boolean; onToggle: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Refresh this page daily"
      disabled={disabled}
      onClick={() => onToggle(!on)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        background: 'transparent',
        color: 'var(--fg-2)',
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: 'inherit',
        padding: '6px 4px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'relative',
          width: 32,
          height: 18,
          borderRadius: 999,
          flexShrink: 0,
          background: on ? 'var(--he-purple)' : 'var(--bg-3)',
          transition: 'background .15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left .15s',
            boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          }}
        />
      </span>
      Daily
    </button>
  );
}

/** Presentational refresh footer: freshness line, schedule control, "Refresh
 *  now", and a failure banner. Pure — takes state + handlers, so the dev gallery
 *  can render every state without a backend. Rendered only for an owned,
 *  refreshable page (a non-null `usePageRefresh` state). */
export function PageRefreshFooter({
  state,
  busy,
  actionError,
  onRefresh,
  onSchedule,
  onEdit,
  dense = false,
}: {
  state: PageRefreshState;
  busy: boolean;
  actionError: boolean;
  onRefresh: () => void;
  onSchedule: (interval: RefreshInterval) => void;
  onEdit: () => void;
  /** Drop the top margin for a single-row context (the artifact wrapper bar). */
  dense?: boolean;
}) {
  const cooldownMs = useCountdown(state.cooldownUntil);
  const cooling = cooldownMs > 0;
  const cooldownMin = Math.ceil(cooldownMs / 60000);
  const failed = state.status === 'failed';
  const needsEdit = failed && state.errorClass === 'non_recoverable';
  const canRefresh = !state.running && !busy && !cooling;

  // The button is the sole "refreshing" indicator (with its spinner); the
  // freshness line keeps showing the last-updated time so the two don't
  // double up while a run is in flight.
  const freshness = `Updated ${formatRelative(state.lastRefreshedAt)}`;

  const scheduleControl = state.canSchedule ? (
    <DailyToggle on={state.interval === 'daily'} disabled={busy || state.running} onToggle={(next) => onSchedule(next ? 'daily' : 'manual')} />
  ) : (
    // Guest: daily is the licensed-tier upsell — read-only chip, matching the
    // visibility control's guest treatment (visible-but-gated, not hidden).
    <span
      aria-label="Daily auto-refresh is available on licensed accounts"
      title="Daily auto-refresh is available on licensed accounts"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--fg-3)', padding: '6px 4px', cursor: 'default' }}
    >
      {/* Guests can't schedule daily; state the limit plainly rather than a
          dead "Upgrade" affordance that looks clickable but does nothing. */}
      Daily · licensed only
    </span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: dense ? 0 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flex: 1, minWidth: 120, fontSize: 12.5, color: 'var(--fg-3)' }}>
          <span>
            {freshness}
            {cooling && !state.running ? ` · ready in ${cooldownMin}m` : ''}
          </span>
        </span>
        {scheduleControl}
        <button
          type="button"
          onClick={onRefresh}
          disabled={!canRefresh}
          title={cooling ? `You can refresh again in ${cooldownMin}m` : 'Re-run this page’s data now'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            border: 'none',
            borderRadius: 9,
            background: canRefresh ? 'rgba(95,38,252,.1)' : 'var(--bg-3)',
            color: canRefresh ? 'var(--he-purple)' : 'var(--fg-3)',
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            padding: '7px 12px',
            cursor: canRefresh ? 'pointer' : 'default',
          }}
        >
          <RefreshGlyph spinning={state.running} />
          {state.running ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {failed &&
        (needsEdit ? (
          <div
            role="status"
            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'rgba(247,144,9,.1)', borderRadius: 10, padding: '9px 12px' }}
          >
            <span style={{ flex: 1, minWidth: 160, fontSize: 12.5, color: 'var(--fg-2)' }}>
              This page couldn’t refresh its data and automatic updates are paused. Editing it with AI can fix the query.
            </span>
            <button
              type="button"
              onClick={onEdit}
              style={{ border: 'none', borderRadius: 9, background: 'var(--he-orange)', color: '#fff', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', padding: '7px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Edit with AI
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--fg-2)', background: 'rgba(247,144,9,.1)', borderRadius: 10, padding: '9px 12px' }}>
            {state.paused
              ? 'The last refresh didn’t complete, so automatic updates are paused. Showing the most recent data — try Refresh now.'
              : 'The last refresh didn’t complete — showing the most recent data. It’ll retry automatically.'}
          </div>
        ))}

      {actionError && (
        <span role="alert" style={{ fontSize: 12, color: 'var(--he-red)' }}>
          Something went wrong — try again.
        </span>
      )}
    </div>
  );
}
